/**
 * ContentBlockItem - Subtle static card with Lucide icons
 */

import { FileText, Play, HelpCircle, FileType, Presentation } from "lucide-react";

// Block type icons (Lucide)
const BLOCK_ICONS: Record<string, React.ReactNode> = {
    quiz: <HelpCircle className="w-4 h-4 text-slate-500" />,
    youtube: <Play className="w-4 h-4 text-slate-500" />,
    note: <FileText className="w-4 h-4 text-slate-500" />,
    pdf: <FileType className="w-4 h-4 text-slate-500" />,
    explainer: <Presentation className="w-4 h-4 text-slate-500" />,
};

const BLOCK_LABELS: Record<string, string> = {
    quiz: "Quiz",
    youtube: "Video",
    note: "Note",
    pdf: "PDF",
    explainer: "Slides",
};

/**
 * Extract a display title from a note block's content.
 * Handles BlockNote/TipTap rich text format with blocks containing text content.
 */
function getNoteDisplayTitle(block: { block_type: string; title: string; content: any }): string {
    // Only apply to note blocks
    if (block.block_type !== "note") {
        return block.title;
    }

    // Content could be an array of blocks (BlockNote format)
    const blocks = Array.isArray(block.content) ? block.content : null;
    if (!blocks || blocks.length === 0) {
        return block.title;
    }

    // Find first block with text content (heading or paragraph)
    for (const contentBlock of blocks) {
        // Skip empty blocks
        if (!contentBlock.content || !Array.isArray(contentBlock.content)) {
            continue;
        }

        // Extract text from the content array
        const textParts = contentBlock.content
            .filter((item: any) => item.type === "text" && item.text)
            .map((item: any) => item.text);

        if (textParts.length > 0) {
            const text = textParts.join("").trim();
            if (text) {
                // Truncate if too long
                return text.length > 80 ? text.slice(0, 77) + "..." : text;
            }
        }
    }

    return block.title;
}

interface ContentBlockItemProps {
    block: {
        id: string;
        block_type: string;
        title: string;
        content: any;
    };
}

export const ContentBlockItem = ({ block }: ContentBlockItemProps) => {
    const icon = BLOCK_ICONS[block.block_type] || <FileText className="w-4 h-4 text-slate-500" />;
    const label = BLOCK_LABELS[block.block_type] || "Item";
    const displayTitle = getNoteDisplayTitle(block);

    return (
        <div className="flex items-center gap-3 p-3.5 bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow hover:border-slate-300">
            {/* Icon */}
            <span className="flex-shrink-0">{icon}</span>

            {/* Title */}
            <span className="text-sm font-medium text-slate-800 truncate flex-1">
                {displayTitle}
            </span>

            {/* Type Label */}
            <span className="text-xs text-slate-400 flex-shrink-0">
                {label}
            </span>
        </div>
    );
};

export default ContentBlockItem;
